import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../assets/styles/Spotlight.scss";

export type SpotlightItem = {
  id: string;
  title: string;
  type?: string;
  difficulty?: string;
  tags?: string[];
};

type Props = {
  open: boolean;
  onClose: () => void;
  query: string;
  setQuery: (q: string) => void;
  items: SpotlightItem[];
};

const MAX_RESULTS = 7;

export default function SpotlightSearch({ open, onClose, query, setQuery, items }: Props) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [active, setActive] = useState(0);

  const q = query.toLowerCase().trim();

  // Searches every writeup, not just the type the page is filtered to — the
  // point of the palette is to reach anything without changing tabs first.
  const results = useMemo(() => {
    const pool = !q
      ? items
      : items.filter(
          (w) =>
            w.title.toLowerCase().includes(q) ||
            (w.tags || []).some((t) => t.toLowerCase().includes(q)) ||
            (w.difficulty || "").toLowerCase().includes(q)
        );
    return pool.slice(0, MAX_RESULTS);
  }, [q, items]);

  useEffect(() => setActive(0), [q]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const go = (item: SpotlightItem) => {
    onClose();
    navigate(`/writeups/${encodeURIComponent(item.id)}`);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      onClose();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (results.length ? (i + 1) % results.length : 0));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (results.length ? (i - 1 + results.length) % results.length : 0));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const hit = results[active];
      if (hit) go(hit);
      else onClose();
    }
  };

  return (
    <div
      className="spotlight"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="spotlight__panel" role="dialog" aria-modal="true" aria-label="Search writeups">
        <div className="spotlight__field">
          <svg className="spotlight__icon" viewBox="0 0 24 24" aria-hidden focusable="false">
            <circle cx="11" cy="11" r="7" />
            <line x1="16.5" y1="16.5" x2="21" y2="21" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search writeups by title, tag or difficulty..."
            aria-label="Search writeups"
          />
          <kbd className="spotlight__hint">esc</kbd>
        </div>

        {results.length > 0 && (
          <ul className="spotlight__results">
            {results.map((r, i) => (
              <li
                key={r.id}
                className={`spotlight__result${i === active ? " spotlight__result--active" : ""}`}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  go(r);
                }}
              >
                <span className="spotlight__result-title">{r.title}</span>
                <span className="spotlight__result-type">
                  {(r.type || "ctf").toLowerCase() === "ctf" ? "CTF" : "Finding"}
                </span>
              </li>
            ))}
          </ul>
        )}

        {q && results.length === 0 && (
          <div className="spotlight__empty">No writeups match “{query}”.</div>
        )}
      </div>
    </div>
  );
}
